import { trustedOrigin, type HostProvider } from '@tasks/bridge-client/host';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Button } from '#components/ui/button';
import { loginWithVitto } from '#lib/auth/auth-api';
import { authSession, type AuthSession } from '#lib/auth/auth-session';
import { ownerOf, vittoSession } from '#lib/auth/vitto-session';
import { type DoorFailure, enterWithVitto } from '#lib/auth/vitto-door';
import { doorMessage } from './door-copy.js';

/** After this long «Signing in» stops being a plausible wait and a retry is offered. */
export const SLOW_AFTER_MS = 4000;

type Phase =
  | { phase: 'entering' }
  | { phase: 'in' }
  | { phase: 'renewing' }
  | { phase: 'failed'; failure: DoorFailure; had: 'entering' | 'in' };

const Frame = ({ children }: { children: ReactNode }) => (
  <main className="grid min-h-dvh place-items-center bg-background px-6">
    <div className="flex max-w-sm flex-col items-center gap-3 text-center">{children}</div>
  </main>
);

const Failure = ({
  failure,
  onRetry,
  vittoOrigin,
}: {
  failure: DoorFailure;
  onRetry: () => void;
  vittoOrigin?: string;
}) => {
  const message = doorMessage(failure);
  const destination = vittoOrigin ? trustedOrigin(vittoOrigin) : undefined;
  return (
    <div role="alert" className="flex flex-col items-center gap-3">
      <h1 className="text-base font-semibold">{message.title}</h1>
      <p className="text-sm text-muted-foreground">{message.detail}</p>
      {!message.retry ? (
        <p className="text-sm text-muted-foreground">
          After updating access in Vitto, check again.
        </p>
      ) : null}
      <div className="flex flex-wrap justify-center gap-2">
        {!message.retry && destination ? (
          <Button asChild className="min-h-11">
            <a href={destination} target="_blank" rel="noopener noreferrer">
              Open Vitto
            </a>
          </Button>
        ) : null}
        <Button
          type="button"
          variant={message.retry ? 'default' : 'outline'}
          className="min-h-11"
          onClick={onRetry}
        >
          {message.retry ? 'Try again' : 'Check access again'}
        </Button>
      </div>
    </div>
  );
};

/**
 * TASKS-APP-FULL-UI-1 U2b — the entrance of an app FRAMED by Vitto. There is no login here and nothing is remembered: every load asks
 * the host for a pass and trades it for the product session (`enterWithVitto`). The tree below renders only once that session exists, so
 * the routes never see an anonymous visitor. When the product session is lost later (its refresh chain died), the door is asked again
 * with `renew`, WITHOUT unmounting the tree: what the person was writing stays; if the host refuses, an overlay says why and the tree
 * stays under it (the same promise the reauth modal makes).
 */
export const EmbeddedGate = ({
  host,
  vittoOrigin,
  session = authSession,
  exchange = loginWithVitto,
  slowAfterMs = SLOW_AFTER_MS,
  onAccountChanged,
  children,
}: {
  host: HostProvider;
  /** Trusted deploy origin, never a URL from the refusal payload. */
  vittoOrigin?: string;
  session?: AuthSession;
  exchange?: (grant: string) => Promise<unknown>;
  slowAfterMs?: number;
  /** Called when a renewal turned out to be ANOTHER account: the owner of the app's cache must forget everything it holds (O5). */
  onAccountChanged?: () => void;
  children: ReactNode;
}) => {
  const [state, setState] = useState<Phase>({ phase: 'entering' });
  const [slow, setSlow] = useState(false);
  // the effect runs twice under StrictMode on the same instance: `running` keeps ONE run in flight and `alive` is a mounted flag (not a cancel flag)
  const running = useRef(false);
  const alive = useRef(false);
  /** Has the person EVER been inside in this document? Then a failure keeps the tree mounted (their draft), however many retries. */
  const inside = useRef(false);
  /** Who entered FIRST in this document (`<workspace>:<user>`): a renewal for anybody else is refused. */
  const owner0 = useRef<string | null>(null);
  /** A ref, not a dependency of `run`: a parent that passes a NEW arrow each render must not re-run the door (T1 of GABO, O2). */
  const accountChanged = useRef(onAccountChanged);
  accountChanged.current = onAccountChanged;
  const sessionState = useSyncExternalStore(
    (listener) => session.subscribe(listener),
    () => session.current,
  );

  const run = useCallback(
    (reason: 'open' | 'renew') => {
      if (running.current) return;
      running.current = true;
      const had = inside.current ? 'in' : 'entering';
      setState(reason === 'renew' ? { phase: 'renewing' } : { phase: 'entering' });
      void enterWithVitto({
        host,
        exchange,
        accept: (pair) => session.accept(pair),
        // a renewal may never change WHO is inside: the mounted tree holds that person's drafts and cache. The comparison is made BEFORE the
        // pair becomes the session (U2b, T1 of GABO, O4): another account's token is never the session of this tree, not even for a tick
        admit: (identity) => owner0.current === null || owner0.current === ownerOf(identity),
        reason,
      }).then((outcome) => {
        running.current = false;
        if (
          !outcome.ok &&
          outcome.failure.kind === 'rejected' &&
          outcome.failure.code === 'account_changed'
        ) {
          // the new pair was never accepted (`admit`); what is left is to drop the old person's session, cache and tree
          session.signOut();
          vittoSession.clear();
          inside.current = false;
          owner0.current = null;
          accountChanged.current?.();
          if (alive.current)
            setState({ phase: 'failed', failure: outcome.failure, had: 'entering' });
          return;
        }
        if (outcome.ok) {
          owner0.current = ownerOf(outcome.identity);
          inside.current = true;
          vittoSession.set({ identity: outcome.identity, label: host.viewerLabel?.() });
        }
        if (!alive.current) return;
        setState(outcome.ok ? { phase: 'in' } : { phase: 'failed', failure: outcome.failure, had });
      });
    },
    [host, exchange, session],
  );

  useEffect(() => {
    alive.current = true;
    run('open'); // StrictMode's second effect is refused by `running`: ONE ask to the host
    return () => {
      alive.current = false;
    };
  }, [run]);

  // the product session was lost while inside: ask for a new pass, quietly
  useEffect(() => {
    if (state.phase === 'in' && sessionState === 'anonymous' && session.wasAuthenticated)
      run('renew');
  }, [state.phase, sessionState, session, run]);

  useEffect(() => {
    if (state.phase !== 'entering') {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(timer);
  }, [state.phase, slowAfterMs]);

  if (state.phase === 'entering')
    return (
      <Frame>
        <div role="status" className="flex flex-col items-center gap-3">
          <h1 className="text-base font-semibold">Signing in with Vitto…</h1>
          {/* no retry here: the host's own wait ends in a refusal (15 s) that offers it, and a second ask while one is in flight is refused */}
          {slow ? (
            <p className="text-sm text-muted-foreground">This is taking longer than usual.</p>
          ) : null}
        </div>
      </Frame>
    );

  if (state.phase === 'failed' && state.had === 'entering')
    return (
      <Frame>
        <Failure vittoOrigin={vittoOrigin} failure={state.failure} onRetry={() => run('open')} />
      </Frame>
    );

  return (
    <>
      {children}
      {state.phase === 'failed' ? (
        <dialog
          open
          aria-modal="true"
          aria-label="Your Vitto session ended"
          className="fixed inset-0 z-50 grid h-full max-h-none w-full max-w-none place-items-center bg-scrim p-4"
        >
          <div className="w-full max-w-sm rounded-lg border border-border bg-card p-6 text-card-foreground shadow-overlay">
            <Failure
              vittoOrigin={vittoOrigin}
              failure={state.failure}
              onRetry={() => run('renew')}
            />
          </div>
        </dialog>
      ) : null}
    </>
  );
};
